import { GridColDef, GridPreProcessEditCellProps, GridRowId } from "@mui/x-data-grid"
import { Box } from "@mui/material"
import { useState } from "react"
import EditableTable from "@components/EditableTable"
import { useGetProductsQuery } from "@store/services/productsService"
import SelectProduct from "@features/administration/products/SelectProduct"
import ProductLabel from "@features/administration/products/SelectProduct/ProductLabel"
import SelectProductFamily from "@features/administration/products/SelectProductFamily"

export type ProductFamily = {
  family: string
  product: string
  id?: number
}

type ProductFamiliesProps = {
  initialData?: ProductFamily[]
  updateCallback: (value: ProductFamily[]) => void
}

export const ProductFamilies = ({ initialData, updateCallback }: ProductFamiliesProps) => {
  const defaultProductFamilies: ProductFamily[] = initialData
    ? initialData.map((item, index) => ({
        family: item.family,
        product: item.family.split("#").slice(0, -1).join("#"), // remove family from org#div#prod#fam key
        id: index,
      }))
    : []
  const [productFamilies, setProductFamilies] = useState<ProductFamily[]>(defaultProductFamilies)
  const { data: productsData } = useGetProductsQuery({ roleHint: "" })
  const products = productsData?.items ?? []

  const columns: GridColDef[] = [
    {
      field: "product",
      headerName: "Product",
      headerClassName: "table-header-cell",
      flex: 1,
      editable: true,
      type: "singleSelect",
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError = params.props.value.length < 1
        return { ...params.props, error: hasError }
      },
      renderEditCell: (params) => {
        return (
          <SelectProduct
            products={products}
            value={params.value}
            hasFocus={params.hasFocus}
            field={params.field}
            gridRowId={params.id}
          />
        )
      },
      renderCell: (params) => {
        const [organisationKey, divisionKey, productKey] = params.value.split("#")
        const selectedProduct = products.find(
          (product) =>
            organisationKey === product.organisation_key &&
            product.division_key === divisionKey &&
            productKey === product.key,
        )

        return <ProductLabel product={selectedProduct} />
      },
    },
    {
      field: "family",
      headerName: "Base Family",
      headerClassName: "table-header-cell",
      flex: 1,
      editable: true,
      type: "singleSelect",
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        const hasError = params.props.value.length < 1
        return { ...params.props, error: hasError }
      },
      renderEditCell: (params) => {
        return (
          <SelectProductFamily
            productKey={params.row.product}
            value={params.value}
            hasFocus={params.hasFocus}
            field={params.field}
            gridRowId={params.id}
          />
        )
      },
      renderCell: (params) => {
        return params.value?.split("#")[3]
      },
    },
  ]

  const handleRowUpdate = (value: ProductFamily) => {
    // TODO update inside this component when we add more granular api
    const families = productFamilies.filter((p: ProductFamily) => p.id !== value.id)
    const updatedRow = { ...value, product: value.product.split("#").slice(-1)[0] }

    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    families.push(updatedRow)
    updateCallback(families)
  }

  const onDelete = (value: GridRowId | null) => {
    // TODO update inside this component when we add more granular api
    updateCallback(productFamilies.filter((p: ProductFamily) => p.id !== value))
  }

  return (
    <Box className={"permissions-table-container"}>
      <EditableTable
        data={productFamilies}
        setData={setProductFamilies}
        columns={columns}
        onRowUpdate={handleRowUpdate}
        onDelete={onDelete}
        emptyObject={{ family: "", product: "" }}
        fieldToFocus={"family"}
      />
    </Box>
  )
}
